import { Alert, Column, Text } from '@amzn/meridian';
import { platformOptions, productLineOptions, programOptions } from 'app.constants';
import { ErrorAlert } from 'components/ErrorAlert';
import { type Dispatch, type FC, type SyntheticEvent, useMemo, useState } from 'react';
import { Col, Form } from 'react-bootstrap';
import { stylesheet } from 'typestyle';
import { validateFileType } from 'utils/assets/validate-file-type';
import { ImageTagMultiselect } from './ImageTagMultiselect';
import type { ImageSelectorModalAction } from './image-selector-modal.actions';
import type { ImageTags } from './image-selector-modal.types';

export type ImagesFromFileSystemSelectorProps = {
  dispatch: Dispatch<ImageSelectorModalAction>;

  files: File[];
  tags: ImageTags;
  makeVisibleForEveryone: boolean;

  multiselect: boolean;
  validFileTypes: string[];
  maxFilesAllowed: number;
};

const css = stylesheet({
  dropZone: {
    $nest: {
      '.alert-hint': {
        backgroundColor: '#f0f0f0',
        borderColor: '#d5d5d5',
        color: '#0c5460',
        fontSize: '$font-size-sm',
      },
      '.btn-icon-danger': {
        $nest: {
          '&:hover': {
            color: 'red',
          },
        },
        alignItems: 'center',
        background: 'none',
        border: 'none',
        color: '#cc0000',
        cursor: 'pointer',
        display: 'flex',
        fontSize: '18px',
        justifyContent: 'center',
        minHeight: '30px',
        minWidth: '30px',
      },
      '.drop-zone': {
        $nest: {
          '.drop-zone-file-input': {
            bottom: '0',
            height: '100%',
            left: '0',
            opacity: '0',
            position: 'absolute',
            right: '0',
            top: '0',
            width: '100%',
          },

          '.fa': {
            color: 'gray',
            fontSize: '48px',
            marginTop: '-15px',
            paddingBottom: '15px',
          },

          '&:focus-within': {
            outline: '-webkit-focus-ring-color auto 5px',
            outlineOffset: '-5px',
          },
          '&:hover': {
            backgroundColor: '#e3eaef',
          },
        },
        alignItems: 'center',
        backgroundColor: '#efefef',
        border: '3px dashed #b5b5b5',
        borderRadius: '10px',
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100% - 15px)',
        justifyContent: 'center',
        position: 'relative',
      },
    },
    height: '100%',
  },
  fileNameAndErrorsContainer: {
    flexBasis: '50%',
    flexGrow: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});

// Max file size supported by media central - https://sage.amazon.com/posts/1507314
export const MEDIA_CENTRAL_MAX_FILE_SIZE_BYTES = 50_000_000;
const maxMediaCentralFileSizeMB = MEDIA_CENTRAL_MAX_FILE_SIZE_BYTES / 1_000_000;

type FileProcessingResult = {
  file: File;
  fileSizeErrorMessage: string | null;
  fileTypeErrorMessage: string | null;
};

/**
 * Processes a file and returns a FileProcessingResult object.
 * @param file - The file to be processed.
 * @returns A Promise that resolves to a FileProcessingResult object.
 *
 * - Rejects if the file.type is not an accepted file type
 * - checks for file size and adds an error message if it exceeds the max file size
 * - checks for file extension and adds an error message if it does not match the actual file type
 *
 * @example
 * const result = await processFile(file);
 */
const processAssetFile = async ({ file, validFileTypes }: { file: File; validFileTypes: string[] }): Promise<FileProcessingResult> => {
  const fileTypeErrorMessage = await validateFileType(file);
  const fileSizeMB = (file.size / 1_000_000).toFixed(2);
  const fileSizeErrorMessage =
    file.size > MEDIA_CENTRAL_MAX_FILE_SIZE_BYTES ? `File (${fileSizeMB} MB) exceeds ${maxMediaCentralFileSizeMB} MB` : null;
  if (!validFileTypes.includes(file.type)) {
    throw `File "${file.name}" has incorrect type of "${file.type}"`;
  }

  return {
    file,
    fileSizeErrorMessage,
    fileTypeErrorMessage,
  };
};

export const ImagesFromFileSystemSelector: FC<ImagesFromFileSystemSelectorProps> = ({
  dispatch,
  files,
  tags,
  multiselect,
  validFileTypes,
  maxFilesAllowed,
}) => {
  const [filenameToSizeErrorMap, setFilenameToSizeErrorMap] = useState(new Map<string, string>());
  const [filenameToExtensionErrorMap, setFilenameToExtensionErrorMap] = useState(new Map<string, string>());

  const selectedFilesHelpText = files && files.length > 1 ? `${files.length} selected files:` : 'Selected file:';
  const fileSizeExceededErrorMessage = useMemo(
    () => `The selected ${files.length === 1 ? 'file exceeds' : 'files exceed'} the ${maxMediaCentralFileSizeMB} MB size limit imposed
    by Media Central and cannot be uploaded. Please remove them to continue.`,
    [
      files.length,
    ],
  );

  const incorrectExtensionErrorMessage: string | null = useMemo(() => {
    const numBrokenFiles = filenameToExtensionErrorMap.size;
    if (numBrokenFiles === 0) return null;
    if (numBrokenFiles === 1) return 'A file has an extension that does not match its true file type. Please remove this file to continue.';
    return `${numBrokenFiles} files have extensions that do not match the true file type. Please remove these files to continue.`;
  }, [
    filenameToExtensionErrorMap.size,
  ]);

  // Prettify the validFileType extensions
  const prettyFileExtensions = useMemo(() => {
    if (validFileTypes) {
      // remove image/ or video/
      const extensions = validFileTypes.map((extension) => extension.split('/')[1]);
      if (extensions.length > 1) {
        return `${extensions
          .slice(0, extensions.length - 1)
          .join(', ')
          .toUpperCase()} and ${extensions[extensions.length - 1].toUpperCase()}`;
      }
      return extensions[0];
    }

    return 'JPG, GIF, or PNG';
  }, [
    validFileTypes,
  ]);

  const fileFormHint = `to select a media file (accepts ${prettyFileExtensions})`;

  const fileAccept = validFileTypes.join(', ');

  async function filesChanged(event: SyntheticEvent<HTMLInputElement>) {
    const fileInput = event.target as HTMLInputElement;

    // Convert iterable fileInput.files into an array
    const filesArray = Array.from(fileInput.files ?? []);
    const fileProcessingPromises = filesArray.map((file) =>
      processAssetFile({
        file,
        validFileTypes,
      }),
    );
    const promiseSettledResults = await Promise.allSettled(fileProcessingPromises);
    const newFilenameToSizeErrorMap = new Map<string, string>();
    const newFilenameToExtensionErrorMap = new Map<string, string>();

    // Array of files that we send back up to parent component
    // Includes files that have size or file type mismatch errors for visibility
    const filesWithValidMimeType: Array<File> = [];

    promiseSettledResults.forEach((result) => {
      if (result.status === 'rejected') {
        // The promise rejects if when the wrong file type is dragged in.
        // Not possible when opening the system's file select dialog.
        // Example: User drags and drops a .pdf or .mp3 file when we only select image files
        console.error('Error processing file:', result.reason);
        return;
      }
      const { value: fileProcessingResult } = result;
      const { file, fileSizeErrorMessage, fileTypeErrorMessage } = fileProcessingResult;
      if (fileSizeErrorMessage) {
        newFilenameToSizeErrorMap.set(file.name, fileSizeErrorMessage);
      }
      if (fileTypeErrorMessage) {
        newFilenameToExtensionErrorMap.set(file.name, fileTypeErrorMessage);
      }
      // Even with error messages, we still want to show the file in the list.
      filesWithValidMimeType.push(file);
    });

    filesWithValidMimeType.slice(0, maxFilesAllowed);
    setFilenameToExtensionErrorMap(newFilenameToExtensionErrorMap);
    setFilenameToSizeErrorMap(newFilenameToSizeErrorMap);

    dispatch({
      files: filesWithValidMimeType,
      hasInvalidFiles: newFilenameToSizeErrorMap.size > 0 || newFilenameToExtensionErrorMap.size > 0,
      type: 'filesSelectedChanged',
    });
  }

  function removeFileClicked(file: File) {
    const fileName = file.name;

    const newFilenameToSizeErrorMap = new Map(filenameToSizeErrorMap);
    newFilenameToSizeErrorMap.delete(fileName);
    setFilenameToSizeErrorMap(newFilenameToSizeErrorMap);

    const newFilenameToExtensionErrorMap = new Map(filenameToExtensionErrorMap);
    newFilenameToExtensionErrorMap.delete(fileName);
    setFilenameToExtensionErrorMap(newFilenameToExtensionErrorMap);

    const newFiles = files.filter((f) => f.name !== file.name);
    dispatch({
      files: newFiles,
      hasInvalidFiles: newFilenameToSizeErrorMap.size > 0 || newFilenameToExtensionErrorMap.size > 0,
      type: 'filesSelectedChanged',
    });
  }

  const setTags = (tagType: keyof ImageTags) => (tags: string[]) =>
    dispatch({
      tags,
      tagType,
      type: 'tagsChanged',
    });

  const setProductLineTags = setTags('productLine');
  const setDeviceTags = setTags('device');
  const setPlatformTags = setTags('platform');
  const setCustomTags = setTags('tags');

  return (
    <div className={css.dropZone}>
      {(!files || files.length === 0) && (
        <form autoComplete="off" className="drop-zone" noValidate>
          <input
            accept={fileAccept}
            autoComplete="off"
            className="drop-zone-file-input"
            id="image-selector-modal-file-input"
            multiple={multiselect}
            onChange={filesChanged}
            type="file"
          />
          <i className="fa fa-images" />
          <label className="drop-title h3" htmlFor="image-selector-modal-file-input">
            Drag & drop or click here
          </label>
          <div className="drop-hint text-muted">{fileFormHint}</div>
          <br />
          <div className="drop-hint text-muted">The maximum accepted file size is 50 MB</div>
        </form>
      )}
      {files && files.length > 0 && (
        <div className="pb-3">
          <Column>
            {files.length >= maxFilesAllowed && (
              <Alert title="Too many files" type="warning">
                Only the {maxFilesAllowed} files shown below will be uploaded.
              </Alert>
            )}
            {filenameToSizeErrorMap.size > 0 && (
              <ErrorAlert contextMessage="File upload failed" error={new Error(fileSizeExceededErrorMessage)} />
            )}
            {filenameToExtensionErrorMap.size > 0 && incorrectExtensionErrorMessage && (
              <ErrorAlert contextMessage="File upload failed" error={new Error(incorrectExtensionErrorMessage)} />
            )}

            <div>
              <Text htmlFor="image-selector-modal-file-list" tag="label">
                {selectedFilesHelpText}
              </Text>
              <ul className="list-group" id="image-selector-modal-file-list">
                {files.map((file) => (
                  <li className="list-group-item d-flex align-items-center justify-content-start" key={file.name}>
                    <div className={css.fileNameAndErrorsContainer}>
                      <Text truncate type="b200">
                        {file.name}
                      </Text>
                      {filenameToSizeErrorMap.has(file.name) && (
                        <Text color="error" type="b200">
                          {filenameToSizeErrorMap.get(file.name)}
                        </Text>
                      )}
                      {filenameToExtensionErrorMap.has(file.name) && (
                        <Text color="error" type="b200">
                          {filenameToExtensionErrorMap.get(file.name)}
                        </Text>
                      )}
                    </div>
                    <button
                      className="fa fa-times btn-icon-danger"
                      onClick={() => removeFileClicked(file)}
                      style={{
                        flexBasis: '10%',
                      }}
                      title="Deselect this file"
                      type="button"
                    />
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h5>
                Tag overrides&nbsp;
                <span className="h5 text-muted font-italic">(optional)</span>
              </h5>

              <div className="form-row">
                <Col sm="4">
                  <ImageTagMultiselect
                    dataCy="input-product-line-tags"
                    description="Product line supports multiple tags."
                    isMulti
                    label="Product line"
                    onChange={setProductLineTags}
                    options={productLineOptions}
                    value={tags.productLine}
                  />
                </Col>
                <Col sm="4">
                  <ImageTagMultiselect
                    dataCy="input-device-tags"
                    isMulti
                    label="Device"
                    onChange={setDeviceTags}
                    options={programOptions}
                    value={tags.device}
                  />
                </Col>
                <Col sm="4">
                  <ImageTagMultiselect
                    dataCy="input-platform-tags"
                    description="Platform only supports one tag."
                    label="Platform"
                    onChange={setPlatformTags}
                    options={platformOptions}
                    value={tags.platform}
                  />
                </Col>
              </div>
              <h5>
                Additional Tags&nbsp;
                <span className="h5 text-muted font-italic">(optional)</span>
              </h5>

              <ImageTagMultiselect dataCy="input-custom-tags" isMulti label="Custom tags" onChange={setCustomTags} value={tags.tags} />

              <h5>
                Permissions&nbsp;
                <span className="h5 text-muted font-italic">(optional)</span>
              </h5>

              <Form.Group controlId="upload-permission">
                <Form.Check label="Make it visible for everyone" type="checkbox" />
              </Form.Group>
            </div>
          </Column>
        </div>
      )}
    </div>
  );
};
